import React, { useEffect, useState, useCallback } from 'react';
import { useExportExcel } from '../hooks/useExportExcel';
import {
  getEmployes, getEmploye, createEmploye, updateEmploye, deleteEmploye,
  getDepartements, getFonctions, getProjets,
  getBureaux, affecterBureau,            // §Bureau
  getHistoriquePostes,                   // §3.1.2
  createHistoriquePoste, updateHistoriquePoste, deleteHistoriquePoste, // §3.1.2 CRUD
  getDocumentsRH, createDocumentRH, updateDocumentRH, deleteDocumentRH, uploadDocumentRH,  // §3.1.3
  getNextMatricule,                      // auto-génération matricule
} from '../services/api';
import { useLocation } from 'react-router-dom';
import ModalDepart from '../components/ModalDepart';

// ─── Constantes ──────────────────────────────────────────────────────────────

const TYPES_CONTRAT = ['CDI','CDD','Stage','Consultant','Bénévole','Prestation de service'];
const TYPES_PIECE   = ['CNI','Passeport','Carte consulaire','Titre de séjour'];
const SITUATIONS    = ['Célibataire','Marié(e)','Divorcé(e)','Veuf(ve)'];
const GS            = ['A+','A-','B+','B-','AB+','AB-','O+','O-'];
const GRADES        = ['—','A1','A2','B1','B2','C1','C2','D',
                       'Agent de maîtrise','Cadre','Cadre supérieur','Directeur'];
const TYPES_DOC     = ['CDD','CDI','Avenant','Titre de séjour','Passeport','Permis de travail','Attestation','Diplôme','Autre'];

// §3.1.2 — ENUM SQL aligné : Recrutement / Promotion / Mutation / Retraite / Autre
// CORRECTION : les valeurs JS Rétrogradation, Affectation, Détachement ont été retirées
// car elles n'existent pas dans l'ENUM SQL → erreur silencieuse en prod.
// Si vous souhaitez les réactiver, exécuter d'abord patch_enum_type_mouvement.sql :
//   ALTER TABLE historique_postes
//     MODIFY COLUMN type_mouvement ENUM(
//       'Recrutement','Promotion','Mutation','Rétrogradation',
//       'Affectation','Détachement','Retraite','Autre'
//     ) NOT NULL;
const TYPES_MVT_SQL = ['Recrutement','Promotion','Mutation','Retraite','Autre'];

const MOIS_NOM = ['','Janvier','Février','Mars','Avril','Mai','Juin',
  'Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

const now = new Date();


const initForm = () => ({
  matricule:'', nom:'', prenoms:'', sexe:'Masculin', date_naissance:'', lieu_naissance:'',
  nationalite:'Ivoirienne', groupe_sanguin:'', num_cnps:'', type_piece:'CNI', num_piece:'',
  date_expiration_piece:'',
  ville:'', commune:'', quartier:'', telephone:'', email:'',
  situation_matrimoniale:'Célibataire', nom_conjoint:'', nom_pere:'', nom_mere:'',
  urgence_nom:'', urgence_tel:'', statut:'Actif',
  categorie:'', projet_id:'',
  type_contrat:'CDI', date_debut:'', date_fin:'',
  date_debut_essai:'', date_fin_essai:'',
  lieu_service:'', lieu_recrutement:'', nb_renouvellements:0,
  renouv1_date_debut:'', renouv1_date_fin:'',
  renouv2_date_debut:'', renouv2_date_fin:'',
  renouv3_date_debut:'', renouv3_date_fin:'',
  departement_id:'', fonction_id:'', salaire_base:'',
  sursalaire:'', indemnite_representation:'', indemnite_transport:'',
  repartition_projets:[], enfants:[],
  bureau_id:'',
  superieur_id:'',
  solde_ca_initial: 22,
  solde_ca_actuel: null,   // §3.46 — lecture seule, calculé par les feuilles de temps
  conges_ca_pris: null,    // §3.46 — cumul annuel des CA posés
});

const initMvtForm = () => ({
  date_effet: '',
  type_mouvement: TYPES_MVT_SQL[0],
  departement: '',
  fonction: '',
  ancien_grade: '',
  nouveau_grade: '',
  motif: '',
});

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toast({ msg, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return <div className={`toast toast-${type}`}>{msg}</div>;
}

// ─── Formulaire document §3.1.3 ───────────────────────────────────────────────
function DocumentForm({ empId, doc, onSaved, onCancel }) {
  const isEdit = !!doc;
  const [form, setForm] = useState({
    type_document: doc?.type_document || TYPES_DOC[0],
    intitule:      doc?.intitule      || '',
    date_emission: doc?.date_emission ? doc.date_emission.slice(0,10) : '',
    date_expiration: doc?.date_expiration ? doc.date_expiration.slice(0,10) : '',
  });
  const [file, setFile]     = useState(null);
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleSave = async () => {
    if (!empId)               { setErrMsg('Identifiant employé manquant'); return; }
    if (!form.intitule.trim()) { setErrMsg("L'intitulé est requis"); return; }
    setSaving(true); setErrMsg('');
    try {
      let docId = doc?.id;
      if (isEdit) {
        await updateDocumentRH(empId, docId, form);
      } else {
        const res = await createDocumentRH(empId, form);
        docId = res.data?.id;
      }
      if (file && docId) {
        const fd = new FormData();
        fd.append('fichier', file);
        await uploadDocumentRH(empId, docId, fd);
      }
      onSaved();
    } catch (err) {
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.error;
      if (status === 404) {
        setErrMsg(
          serverMsg ||
          'Mouvement introuvable (404) — vérifiez que la route PUT /employes/:id/historique/:id est déclarée dans le routeur PHP (voir router_historique_snippet.php).'
        );
      } else {
        setErrMsg(serverMsg || err?.message || "Erreur lors de l'enregistrement");
      }
    }
    setSaving(false);
  };

  return (
    <div style={{ background:'#f0f6ff', border:'1px solid #c7d8f0', borderRadius:10, padding:16, marginBottom:14 }}>
      <div style={{ fontWeight:700, fontSize:13, marginBottom:12, color:'#1a3a5c' }}>
        {isEdit ? '✏️ Modifier le document' : '📎 Nouveau document'}
      </div>
      <div className="form-grid">
        <div className="fg"><label>Type *</label><select value={form.type_document} onChange={f('type_document')}>{TYPES_DOC.map(t => <option key={t}>{t}</option>)}</select></div>
        <div className="fg"><label>Intitulé *</label><input value={form.intitule} onChange={f('intitule')} placeholder="Ex: CNI n°CI-0123456789" /></div>
      </div>
      <div className="form-grid">
        <div className="fg"><label>Date d'émission</label><input type="date" value={form.date_emission} onChange={f('date_emission')} /></div>
        <div className="fg"><label>Date d'expiration</label><input type="date" value={form.date_expiration} onChange={f('date_expiration')} /></div>
      </div>
      <div className="fg" style={{ marginTop:10 }}>
        <label>📁 Fichier joint (PDF, image…)</label>
        <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e => setFile(e.target.files[0] || null)} style={{ fontSize:13, marginTop:4 }} />
        {isEdit && doc.fichier_nom && !file && (
          <div style={{ fontSize:11, color:'#555', marginTop:4 }}>Fichier actuel : <strong>{doc.fichier_nom}</strong> — sélectionnez un nouveau fichier pour remplacer</div>
        )}
      </div>
      {errMsg && <div style={{ color:'#c62828', background:'#ffebee', borderRadius:6, padding:'6px 10px', fontSize:12, marginTop:10 }}>⚠ {errMsg}</div>}
      <div style={{ display:'flex', gap:8, marginTop:12 }}>
        <button className="btn btn-gray btn-sm" onClick={onCancel} disabled={saving}>Annuler</button>
        <button className="btn btn-green btn-sm" onClick={handleSave} disabled={saving}>
          {saving ? '⏳ Enregistrement…' : (isEdit ? '💾 Mettre à jour' : '💾 Enregistrer')}
        </button>
      </div>
    </div>
  );
}

// ─── Formulaire mouvement §3.1.2 ─────────────────────────────────────────────
function MouvementForm({ empId, mvt, depts, fonctions, onSaved, onCancel }) {
  const isEdit = !!mvt;
  const [form, setForm] = useState(mvt ? {
    date_effet:      mvt.date_effet ? mvt.date_effet.slice(0,10) : '',
    type_mouvement:  mvt.type_mouvement || TYPES_MVT_SQL[0],
    departement:     mvt.departement    || '',
    fonction:        mvt.fonction       || '',
    ancien_grade:    mvt.ancien_grade   || '',
    nouveau_grade:   mvt.nouveau_grade  || '',
    motif:           mvt.motif          || '',
  } : initMvtForm());
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.date_effet)      { setErrMsg('La date d\'effet est requise'); return; }
    if (!form.type_mouvement)  { setErrMsg('Le type de mouvement est requis'); return; }
    setSaving(true); setErrMsg('');
    try {
      if (isEdit) {
        await updateHistoriquePoste(empId, mvt.id, form);
      } else {
        await createHistoriquePoste(empId, form);
      }
      onSaved();
    } catch (err) {
      setErrMsg(err?.response?.data?.error || err?.message || "Erreur lors de l'enregistrement");
    }
    setSaving(false);
  };

  return (
    <div style={{ background:'#f0f6ff', border:'1px solid #c7d8f0', borderRadius:10, padding:16, marginBottom:14 }}>
      <div style={{ fontWeight:700, fontSize:13, marginBottom:12, color:'#1a3a5c' }}>
        {isEdit ? '✏️ Modifier le mouvement' : '➕ Nouveau mouvement de carrière'}
      </div>
      <div className="form-grid">
        <div className="fg">
          <label>Date d'effet *</label>
          <input type="date" value={form.date_effet} onChange={f('date_effet')} />
        </div>
        <div className="fg">
          <label>Type de mouvement *</label>
          <select value={form.type_mouvement} onChange={f('type_mouvement')}>
            {TYPES_MVT_SQL.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
      </div>
      <div className="form-grid">
        <div className="fg">
          <label>Département</label>
          {depts.length > 0
            ? <select value={form.departement} onChange={f('departement')}>
                <option value="">—</option>
                {depts.map(d => <option key={d.id} value={d.nom}>{d.nom}</option>)}
              </select>
            : <input value={form.departement} onChange={f('departement')} placeholder="Département" />}
        </div>
        <div className="fg">
          <label>Fonction / Poste</label>
          {fonctions.length > 0
            ? <select value={form.fonction} onChange={f('fonction')}>
                <option value="">—</option>
                {fonctions.map(fn => <option key={fn.id} value={fn.intitule}>{fn.intitule}</option>)}
              </select>
            : <input value={form.fonction} onChange={f('fonction')} placeholder="Fonction" />}
        </div>
      </div>
      <div className="form-grid">
        <div className="fg">
          <label>Ancien grade</label>
          <select value={form.ancien_grade} onChange={f('ancien_grade')}>
            {GRADES.map(g => <option key={g} value={g === '—' ? '' : g}>{g}</option>)}
          </select>
        </div>
        <div className="fg">
          <label>Nouveau grade</label>
          <select value={form.nouveau_grade} onChange={f('nouveau_grade')}>
            {GRADES.map(g => <option key={g} value={g === '—' ? '' : g}>{g}</option>)}
          </select>
        </div>
      </div>
      <div className="fg" style={{ marginTop:8 }}>
        <label>Motif / Observations</label>
        <textarea
          value={form.motif}
          onChange={f('motif')}
          rows={3}
          placeholder="Motif du mouvement, décision, notes…"
          style={{ padding:'8px 10px', border:'1.5px solid #cbd5e1', borderRadius:7, fontSize:13,
            resize:'vertical', fontFamily:'inherit', outline:'none' }}
        />
      </div>
      {errMsg && <div style={{ color:'#c62828', background:'#ffebee', borderRadius:6, padding:'6px 10px', fontSize:12, marginTop:10 }}>⚠ {errMsg}</div>}
      <div style={{ display:'flex', gap:8, marginTop:12 }}>
        <button className="btn btn-gray btn-sm" onClick={onCancel} disabled={saving}>Annuler</button>
        <button className="btn btn-green btn-sm" onClick={handleSave} disabled={saving}>
          {saving ? '⏳ Enregistrement…' : (isEdit ? '💾 Mettre à jour' : '💾 Enregistrer')}
        </button>
      </div>
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────
export default function EmployesPage() {
  const [employes, setEmployes]     = useState([]);
  const [depts, setDepts]           = useState([]);
  const [fonctions, setFonctions]   = useState([]);
  const [bureaux, setBureaux]       = useState([]);
  const [projets, setProjets]       = useState([]);
  const [superieurs, setSuperieurs] = useState([]); // liste complète pour le choix du supérieur hiérarchique
  const [loading, setLoading]       = useState(true);

  // ── Filtres liste ──
  const [search, setSearch]               = useState('');
  const [filterStatut, setFilterStatut]   = useState('');   // §Filtre statut (Actif/Inactif/Suspendu)
  const [filterMois, setFilterMois]       = useState('');   // §Filtre mois embauche
  const [filterAnnee, setFilterAnnee]     = useState('');   // §Filtre année embauche
  const [filterContrat, setFilterContrat] = useState('');
  const [filterDept, setFilterDept]       = useState('');
  const [filterBureau, setFilterBureau]   = useState('');

  // ── Tri colonnes ──
  const [sortCol, setSortCol]   = useState('');
  const [sortDir, setSortDir]   = useState('asc');

  const handleSort = col => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const sortedEmployes = React.useMemo(() => {
    if (!sortCol) return employes;
    return [...employes].sort((a, b) => {
      let va = '', vb = '';
      if      (sortCol === 'nom')         { va = (a.nom||'')+' '+(a.prenoms||''); vb = (b.nom||'')+' '+(b.prenoms||''); }
      else if (sortCol === 'matricule')   { va = a.matricule  || ''; vb = b.matricule  || ''; }
      else if (sortCol === 'departement') { va = a.departement|| ''; vb = b.departement|| ''; }
      else if (sortCol === 'bureau')      { va = a.bureau_code|| ''; vb = b.bureau_code|| ''; }
      else if (sortCol === 'contrat')     { va = a.type_contrat||''; vb = b.type_contrat||''; }
      const cmp = va.localeCompare(vb, 'fr', { sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [employes, sortCol, sortDir]);

  const [page, setPage]             = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [subTab, setSubTab]         = useState('list');
  const [formTab, setFormTab]       = useState('identite');
  const [editId, setEditId]         = useState(null);
  const [viewData, setViewData]     = useState(null);
  const [form, setForm]             = useState(initForm());
  const [saving, setSaving]         = useState(false);
  const [toast, setToast]           = useState(null);

  // §3.1.2 & §3.1.3 — données panneau view
  const [historique, setHistorique]       = useState([]);
  const [showMvtForm, setShowMvtForm]     = useState(false);    // §3.1.2 formulaire ajout
  const [editMvt, setEditMvt]             = useState(null);     // §3.1.2 mouvement en édition
  const [documents, setDocuments]         = useState([]);
  const [showDocForm, setShowDocForm]     = useState(false);
  const [editDoc, setEditDoc]             = useState(null);
  const [viewTab, setViewTab]             = useState('info');   // 'info' | 'historique' | 'documents'
  const [showModalDepart, setShowModalDepart] = useState(false);  // §Départ

  const years = Array.from({ length: 10 }, (_, i) => now.getFullYear() - 4 + i);

  const showToast = (msg, type = 'success') => setToast({ msg, type });

  // ── Export Excel ──────────────────────────────────────────────────────────
  const { exporting, exportToExcel } = useExportExcel();   // §3.52

  const exportExcel = useCallback(async () => {
    try {
      // Récupérer TOUTES les pages avec les filtres actifs
      const params = {
        q: search,
        departement_id: filterDept,
        bureau_id: filterBureau,
        page: 1,
        limit: 10000,
      };
      if (filterStatut)  params.statut       = filterStatut;
      if (filterContrat) params.type_contrat = filterContrat;   // §3.53
      if (filterMois)   params.mois_debut  = filterMois;
      if (filterAnnee)  params.annee_debut = filterAnnee;

      const r = await getEmployes(params);
      const raw = r.data;
      const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];

      const fmtD = d => d ? new Date(d).toLocaleDateString('fr-FR') : '';

      // §3.52 — export brandé GFM3 (bandeau, en-têtes, filtre, impression)
      const rows = list.map(e => ({
        matricule:      e.matricule    || '',
        nom:            e.nom          || '',
        prenoms:        e.prenoms      || '',
        sexe:           e.sexe         || '',
        date_naissance: fmtD(e.date_naissance),
        ville:          e.ville        || '',
        telephone:      e.telephone    || '',
        email:          e.email        || '',
        statut:         e.statut       || '',
        departement:    e.departement  || '',
        fonction:       e.fonction     || '',
        bureau_nom:     e.bureau_nom   || '',
        bureau_code:    e.bureau_code  || '',
        type_contrat:   e.type_contrat || '',
        salaire_base:   e.salaire_base != null ? Number(e.salaire_base) : '',
        date_debut:     fmtD(e.date_debut),
        date_fin:       fmtD(e.date_fin),
      }));

      const date = new Date().toISOString().slice(0, 10);

      await exportToExcel({
        rows,
        filename:  `GFM3_Fiches_Employes_${date}.xlsx`,
        sheetName: 'Employés',
        title:     'FICHES SIGNALÉTIQUES DU PERSONNEL',
        columns: [
          { key: 'matricule',      label: 'Matricule',           width: 12 },
          { key: 'nom',            label: 'Nom',                 width: 18 },
          { key: 'prenoms',        label: 'Prénoms',             width: 24 },
          { key: 'sexe',           label: 'Sexe',                width: 10 },
          { key: 'date_naissance', label: 'Date de naissance',   width: 16 },
          { key: 'ville',          label: 'Ville',               width: 16 },
          { key: 'telephone',      label: 'Téléphone',           width: 15 },
          { key: 'email',          label: 'Email',               width: 30 },
          { key: 'statut',         label: 'Statut',              width: 11 },
          { key: 'departement',    label: 'Département',         width: 20 },
          { key: 'fonction',       label: 'Fonction',            width: 26 },
          { key: 'bureau_nom',     label: 'Bureau',              width: 20 },
          { key: 'bureau_code',    label: 'Code',                width: 9  },
          { key: 'type_contrat',   label: 'Type de contrat',     width: 15 },
          { key: 'salaire_base',   label: 'Salaire de base',     width: 16 },
          { key: 'date_debut',     label: 'Début de contrat',    width: 16 },
          { key: 'date_fin',       label: 'Fin de contrat',      width: 16 },
        ],
      });

      showToast(`${list.length} fiche(s) exportée(s) ✓`);
    } catch (err) {
      console.error(err);
      showToast('Erreur lors de l\'export', 'error');
    }
  }, [search, filterDept, filterBureau, filterContrat, filterStatut, filterMois, filterAnnee, exportToExcel]);

  // Rechargement documents
  const reloadDocs = useCallback((empId) => {
    getDocumentsRH(empId).then(r => {
      const raw = r.data;
      setDocuments(Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : []);
    }).catch(() => setDocuments([]));
  }, []);

  // Rechargement historique §3.1.2
  const reloadHistorique = useCallback((empId) => {
    // §3.54 — plus aucune donnée de démonstration : on affiche l'état réel.
    getHistoriquePostes(empId)
      .then(rh => {
        const raw = rh.data;
        const arr = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
        setHistorique(arr);
      })
      .catch(() => setHistorique([]));
  }, []);

  const location = useLocation();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        q: search,
        departement_id: filterDept,
        bureau_id: filterBureau,
        page,
        limit: 20,
      };
      // §Filtre statut
      if (filterStatut) params.statut = filterStatut;
      // §3.53 — filtre type de contrat
      if (filterContrat) params.type_contrat = filterContrat;
      // §Filtre mois / année embauche
      if (filterMois)  params.mois_debut  = filterMois;
      if (filterAnnee) params.annee_debut = filterAnnee;

      const r = await getEmployes(params);
      const raw = r.data;
      const list = Array.isArray(raw) ? raw
                 : Array.isArray(raw?.data) ? raw.data
                 : [];
      setEmployes(list);
      setTotalPages(raw?.totalPages || raw?.total_pages || 1);
    } catch (err) {
      console.error('Erreur chargement employés:', err);
      setEmployes([]);
      setTotalPages(1);
    }
    setLoading(false);
  }, [search, filterDept, filterBureau, filterContrat, filterStatut, filterMois, filterAnnee, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getDepartements().then(r => setDepts(r.data));
    getFonctions().then(r => setFonctions(r.data));
    getBureaux().then(r => setBureaux(r.data)).catch(() => setBureaux([]));
    getEmployes({ limit: 1000 }).then(r => {
      const raw = r.data;
      setSuperieurs(Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : []);
    }).catch(() => setSuperieurs([]));
    getProjets().then(r => {
      const raw = r.data;
      setProjets(Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : []);
    }).catch(() => setProjets([]));
  }, []);

  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const openNew = async () => {
    const base = initForm();
    try {
      const r = await getNextMatricule();
      base.matricule = r.data?.matricule || '';
    } catch (_) { /* on laisse vide si l'API échoue */ }
    setForm(base);
    setEditId(null);
    setFormTab('identite');
    setViewData(null);
    setSubTab('form');
  };

  const openEdit = useCallback(async id => {
    try {
      const r = await getEmploye(id);
      const e = r.data;
      setForm({
        ...initForm(), ...e,
        type_contrat:       e.type_contrat       || 'CDI',
        salaire_base:       e.salaire_base        || '',
        sursalaire:               e.sursalaire               || '',
        indemnite_representation: e.indemnite_representation || '',
        indemnite_transport:      e.indemnite_transport      || '',
        repartition_projets: Array.isArray(e.repartition_projets)
          ? e.repartition_projets.map(r => ({ projet_id: String(r.projet_id || ''), pourcentage: r.pourcentage != null ? Number(r.pourcentage) : '' }))
          : [],
        departement_id:     e.departement_id      || '',
        fonction_id:        e.fonction_id         || '',
        categorie:          e.categorie           || '',
        projet_id:          e.projet_id           || '',
        date_debut:         e.date_debut          || '',
        date_fin:           e.date_fin            || '',
        date_debut_essai:   e.date_debut_essai    || '',
        date_fin_essai:     e.date_fin_essai      || '',
        date_expiration_piece: e.date_expiration_piece || '',
        lieu_service:       e.lieu_service        || '',
        lieu_recrutement:   e.lieu_recrutement    || '',
        nb_renouvellements: e.nb_renouvellements  || 0,
        renouv1_date_debut: e.renouv1_date_debut  || '',
        renouv1_date_fin:   e.renouv1_date_fin    || '',
        renouv2_date_debut: e.renouv2_date_debut  || '',
        renouv2_date_fin:   e.renouv2_date_fin    || '',
        renouv3_date_debut: e.renouv3_date_debut  || '',
        renouv3_date_fin:   e.renouv3_date_fin    || '',
        enfants:            e.enfants             || [],
        bureau_id:          e.bureau_id           || '',
        superieur_id:       e.superieur_id        || '',
        solde_ca_initial:   e.solde_ca_initial != null ? e.solde_ca_initial : 22,
        // §3.46 — lecture seule : solde actuel reporté des feuilles de temps
        solde_ca_actuel:    e.solde_ca_actuel != null ? e.solde_ca_actuel : null,
        conges_ca_pris:     e.conges_ca_pris  != null ? e.conges_ca_pris  : null,
      });
      setEditId(id);
      setFormTab('identite');
      setViewData(null);
      setSubTab('form');
    } catch (err) { console.error(err); }
  }, []);

  const openView = useCallback(async id => {
    try {
      const r = await getEmploye(id);
      setViewData(r.data);
      setViewTab('info');
      setHistorique([]);
      setDocuments([]);
      setShowMvtForm(false);
      setEditMvt(null);
      setSubTab('view');

      reloadHistorique(id);
      getDocumentsRH(id)
        .then(rd => {
          const raw = rd.data;
          setDocuments(Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : []);
        })
        .catch(() => setDocuments([]));
    } catch (err) { console.error(err); }
  }, [reloadHistorique]);

  // Ouverture automatique depuis Dashboard
  useEffect(() => {
    const openId = location.state?.openId;
    if (openId) {
      openView(openId);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [openView, location.state?.openId]);

  const handleSave = async () => {
    // §3.57 — La répartition du coût par projet doit totaliser exactement 100 %.
    const lignes = (form.repartition_projets || []).filter(r => r.projet_id);
    if (lignes.length) {
      const sommeProjets = lignes.reduce((s, x) => s + (parseFloat(x.pourcentage) || 0), 0);
      const arrondi = Math.round(sommeProjets * 100) / 100;

      if (arrondi > 100) {
        showToast(
          `Répartition invalide : la somme des projets est de ${arrondi}%. `
          + `Elle ne peut pas dépasser 100%. Corrigez les pourcentages avant d'enregistrer.`,
          'error'
        );
        setFormTab('contrat');
        return;
      }
      if (lignes.some(r => !(parseFloat(r.pourcentage) > 0))) {
        showToast(
          'Répartition invalide : chaque projet sélectionné doit avoir un pourcentage supérieur à 0.',
          'error'
        );
        setFormTab('contrat');
        return;
      }
      // Le complément est imputé à GFM3 Institutionnel : total = 100 % garanti.
    }

    setSaving(true);
    try {
      if (editId) await updateEmploye(editId, form);
      else await createEmploye(form);
      showToast(editId ? 'Employé mis à jour' : 'Employé créé');
      setSubTab('list');
      setEditId(null);
      load();
    } catch (err) { showToast(err.response?.data?.error || 'Erreur', 'error'); }
    setSaving(false);
  };

  const contratBadge = t => {
    const m = {
      CDI: 'blue', CDD: 'orange', Stage: 'gray',
      Consultant: 'green', 'Bénévole': 'purple', 'Prestation de service': 'teal'
    };
    return t ? <span className={`badge badge-${m[t] || 'gray'}`}>{t}</span> : '—';
  };

  const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

  const docStatutColor = s => {
    if (s === 'Expiré') return { color:'#c62828', background:'#ffebee' };
    if (s === 'Bientôt expiré') return { color:'#E65100', background:'#FFF3E0' };
    return { color:'#2e7d32', background:'#e8f5e9' };
  };

  // Couleur badge mouvement
  const mvtBadgeClass = t => {
    const m = {
      Recrutement: 'green',
      Promotion:   'green',
      Mutation:    'blue',
      Retraite:    'orange',
      Autre:       'gray',
    };
    return `badge badge-${m[t] || 'gray'}`;
  };

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      <div className="page-header">
        <div><h2>Fiche signalétique individuelle</h2><p>Créer, consulter ou modifier une fiche employé</p></div>
        <button className="btn btn-blue" onClick={openNew}>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="#fff"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
          Nouvelle fiche
        </button>
      </div>

      <div className="sub-tabs">
        <button className={`sub-tab${subTab === 'list'  ? ' active' : ''}`} onClick={() => setSubTab('list')}>Toutes les fiches</button>
        <button className={`sub-tab${subTab === 'form'  ? ' active' : ''}`} onClick={openNew}>Saisie / Modification</button>
        {viewData && <button className={`sub-tab${subTab === 'view' ? ' active' : ''}`} onClick={() => setSubTab('view')}>Détail</button>}
      </div>

      {/* ════ LISTE ════ */}
      {subTab === 'list' && (
        <div className="card">

          {/* ── Barre de recherche & filtres — style TempsPage ── */}
          <div className="crud-bar" style={{ flexWrap:'wrap', gap:8 }}>
            {/* Recherche texte */}
            <div className="crud-search-icon">
              <svg viewBox="0 0 24 24">
                <path d="M15.5 14h-.79l-.28-.27A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
              <input
                type="text"
                placeholder="Rechercher matricule, nom…"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                style={{ width: 240 }}
              />
            </div>

            {/* Filtre statut employé */}
            <select value={filterStatut} onChange={e => { setFilterStatut(e.target.value); setPage(1); }}>
              <option value="">Tous les statuts</option>
              <option value="Actif">Actif</option>
              <option value="Inactif">Inactif</option>
              <option value="Suspendu">Suspendu</option>
            </select>

            {/* Filtre mois d'embauche */}
            <select value={filterMois} onChange={e => { setFilterMois(e.target.value); setPage(1); }}>
              <option value="">Tous les mois</option>
              {MOIS_NOM.slice(1).map((m, i) => (
                <option key={i + 1} value={i + 1}>{m}</option>
              ))}
            </select>

            {/* Filtre année d'embauche */}
            <select value={filterAnnee} onChange={e => { setFilterAnnee(e.target.value); setPage(1); }}>
              <option value="">Toutes les années</option>
              {years.map(y => <option key={y}>{y}</option>)}
            </select>

            <span style={{ marginLeft:'auto', fontSize:12, color:'#666', whiteSpace:'nowrap' }}>{employes.length} fiche(s)</span>
            <button
              className="btn btn-green btn-sm"
              onClick={exportExcel}
              disabled={exporting}
              title="Exporter la liste filtrée vers Excel"
              style={{ marginLeft:8, display:'flex', alignItems:'center', gap:5 }}
            >
              {exporting
                ? <><span className="spinner" style={{ width:12, height:12, borderWidth:2 }} />Export…</>
                : <>
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 7V3.5L18.5 9H13zm-3 8v-2H8v-2h2v-2h2v2h2v2h-2v2h-2z"/>
                    </svg>
                    Exporter Excel
                  </>
              }
            </button>
          </div>

          {/* Filtres secondaires (contrat / département / bureau) */}
          <div style={{ display:'flex', gap:8, padding:'0 0 10px', flexWrap:'wrap' }}>
            <select value={filterContrat} onChange={e => { setFilterContrat(e.target.value); setPage(1); }}>
              <option value="">Tous les contrats</option>
              {TYPES_CONTRAT.map(t => <option key={t}>{t}</option>)}
            </select>
            <select value={filterDept} onChange={e => { setFilterDept(e.target.value); setPage(1); }}>
              <option value="">Tous les départements</option>
              {depts.map(d => <option key={d.id} value={d.id}>{d.nom}</option>)}
            </select>
            <select value={filterBureau} onChange={e => { setFilterBureau(e.target.value); setPage(1); }}>
              <option value="">Tous les bureaux</option>
              {bureaux.map(b => <option key={b.id} value={b.id}>{b.est_siege ? '🏢 ' : '📍 '}{b.nom}</option>)}
            </select>
          </div>

          {/* Tableau */}
          <div className="table-wrap">
            {loading ? <div className="empty"><span className="spinner spinner-dark" /></div> : (
              <table>
                <thead>
                  <tr>
                    {[
                      { key:'nom',         label:'Employé(e)' },
                      { key:'matricule',   label:'Matricule'  },
                      { key:'departement', label:'Département'},
                      { key:'bureau',      label:'Bureau'     },
                      { key:'contrat',     label:'Contrat'    },
                    ].map(col => (
                      <th key={col.key}
                        onClick={() => handleSort(col.key)}
                        style={{ cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }}
                        title={`Trier par ${col.label}`}
                      >
                        {col.label}{' '}
                        {sortCol === col.key
                          ? (sortDir === 'asc' ? '▲' : '▼')
                          : <span style={{ opacity:0.25 }}>⇅</span>}
                      </th>
                    ))}
                    <th>Téléphone</th>
                    <th>Email</th>
                    <th style={{ textAlign:'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {employes.length === 0
                    ? <tr><td colSpan={8} className="empty">Aucune fiche enregistrée</td></tr>
                    : sortedEmployes.map(e => (
                      <tr key={e.id}>
                        <td>
                          <div className="flex-c">
                            <div className="avatar" style={{ width:30, height:30, fontSize:11 }}>{e.nom?.[0]}{e.prenoms?.[0]}</div>
                            <div>
                              <div style={{ fontWeight:700 }}>{e.nom} {e.prenoms}</div>
                              <div style={{ fontSize:11, color:'#888' }}>
                                {e.statut === 'Actif'
                                  ? <><span className="status-dot dot-green" />Actif</>
                                  : <><span className="status-dot dot-red" />{e.statut}</>}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ fontFamily:'monospace', fontWeight:700, color:'#1565C0' }}>{e.matricule}</td>
                        <td style={{ fontSize:12 }}>{e.departement || '—'}</td>
                        <td style={{ fontSize:12 }}>
                          {e.bureau_nom
                            ? <span title={e.bureau_ville}>{e.est_siege ? '🏢' : '📍'} {e.bureau_code}</span>
                            : <span style={{ color:'#aaa' }}>—</span>}
                        </td>
                        <td>{contratBadge(e.type_contrat)}</td>
                        <td style={{ fontSize:12 }}>{e.telephone || '—'}</td>
                        <td style={{ fontSize:12 }}>{e.email || '—'}</td>
                        <td>
                          <div className="act-cell">
                            <button className="btn-icon btn-icon-view" title="Voir" onClick={() => openView(e.id)}>
                              <svg viewBox="0 0 24 24"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5z"/></svg>
                            </button>
                            <button className="btn-icon btn-icon-edit" title="Modifier" onClick={() => openEdit(e.id)}>
                              <svg viewBox="0 0 24 24"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
                            </button>
                            <button className="btn-icon btn-icon-del" title="Désactiver"
                              onClick={() => window.confirm('Désactiver ?') && deleteEmploye(e.id).then(load)}>
                              <svg viewBox="0 0 24 24"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>

          {totalPages > 1 && (
            <div style={{ display:'flex', gap:6, justifyContent:'center', marginTop:12 }}>
              <button className="btn btn-gray btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Préc.</button>
              <span style={{ padding:'4px 10px', fontSize:13 }}>Page {page}/{totalPages}</span>
              <button className="btn btn-gray btn-sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Suiv. →</button>
            </div>
          )}
        </div>
      )}

      {/* ════ FORMULAIRE ════ */}
      {subTab === 'form' && (
        <>
          {editId && (
            <div style={{ background:'#e3f2fd', border:'1.5px solid #1565C0', borderRadius:6, padding:'10px 16px', marginBottom:12, fontSize:13, color:'#1565C0', fontWeight:700 }}>
              ✏️ Modification de la fiche — {form.nom} {form.prenoms}
            </div>
          )}

          <div className="sub-tabs" style={{ marginBottom:0 }}>
            {['identite','piece','contact','famille','rh'].map(t => (
              <button key={t} className={`sub-tab${formTab === t ? ' active' : ''}`} onClick={() => setFormTab(t)}>
                {{'identite':'Identité','piece':"Pièce d'identité",'contact':'Contact','famille':'Situation familiale','rh':'Infos RH'}[t]}
              </button>
            ))}
          </div>

          {formTab === 'identite' && (
            <div className="card">
              <div className="card-title">Identité</div>
              <div className="form-grid">
                <div className="fg"><label>Nom</label><input value={form.nom} onChange={f('nom')} placeholder="Nom de famille"/></div>
                <div className="fg"><label>Prénoms</label><input value={form.prenoms} onChange={f('prenoms')} placeholder="Prénoms"/></div>
              </div>
              <div className="form-grid three">
                <div className="fg"><label>Sexe</label><select value={form.sexe} onChange={f('sexe')}><option>Masculin</option><option>Féminin</option></select></div>
                <div className="fg"><label>Date de naissance</label><input type="date" value={form.date_naissance} onChange={f('date_naissance')}/></div>
                <div className="fg"><label>Lieu de naissance</label><input value={form.lieu_naissance} onChange={f('lieu_naissance')}/></div>
              </div>
              <div className="form-grid">
                <div className="fg"><label>Nom du père</label><input value={form.nom_pere} onChange={f('nom_pere')}/></div>
                <div className="fg"><label>Nom de la mère</label><input value={form.nom_mere} onChange={f('nom_mere')}/></div>
              </div>
              <div className="form-grid three">
                <div className="fg"><label>Nationalité</label><input value={form.nationalite} onChange={f('nationalite')}/></div>
                <div className="fg"><label>Groupe sanguin</label><select value={form.groupe_sanguin} onChange={f('groupe_sanguin')}><option value="">—</option>{GS.map(g => <option key={g}>{g}</option>)}</select></div>
                <div className="fg"><label>N° CNPS</label><input value={form.num_cnps} onChange={f('num_cnps')}/></div>
              </div>
            </div>
          )}

          {formTab === 'piece' && (
            <div className="card">
              <div className="card-title">Pièce d'identité</div>
              <div className="form-grid three">
                <div className="fg"><label>Nature pièce</label><select value={form.type_piece} onChange={f('type_piece')}>{TYPES_PIECE.map(t => <option key={t}>{t}</option>)}</select></div>
                <div className="fg"><label>Numéro</label><input value={form.num_piece} onChange={f('num_piece')}/></div>
                <div className="fg">
                  <label>Date d'expiration <span style={{ fontSize:10, background:'#dbeafe', color:'#1e40af', borderRadius:10, padding:'1px 6px', fontWeight:700 }}>§3.1.3</span></label>
                  <input type="date" value={form.date_expiration_piece} onChange={f('date_expiration_piece')}/>
                </div>
              </div>
            </div>
          )}

          {formTab === 'contact' && (
            <div className="card">
              <div className="card-title">Adresse & Contact</div>
              <div className="form-grid three">
                <div className="fg"><label>Ville</label><input value={form.ville} onChange={f('ville')}/></div>
                <div className="fg"><label>Commune</label><input value={form.commune} onChange={f('commune')}/></div>
                <div className="fg"><label>Quartier</label><input value={form.quartier} onChange={f('quartier')}/></div>
              </div>
              <div className="form-grid">
                <div className="fg"><label>Téléphone</label><input value={form.telephone} onChange={f('telephone')}/></div>
                <div className="fg"><label>Email</label><input type="email" value={form.email} onChange={f('email')}/></div>
              </div>
              <div className="card-title" style={{ marginTop:12 }}>Personne à contacter en cas d'urgence</div>
              <div className="form-grid">
                <div className="fg"><label>Nom complet</label><input value={form.urgence_nom} onChange={f('urgence_nom')}/></div>
                <div className="fg"><label>Téléphone</label><input value={form.urgence_tel} onChange={f('urgence_tel')}/></div>
              </div>
            </div>
          )}

          {formTab === 'famille' && (
            <div className="card">
              <div className="card-title">Situation familiale</div>
              <div className="form-grid three">
                <div className="fg">
                  <label>Situation matrimoniale</label>
                  <select value={form.situation_matrimoniale} onChange={f('situation_matrimoniale')}>
                    {SITUATIONS.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="fg"><label>Nom du/de la conjoint(e)</label><input value={form.nom_conjoint} onChange={f('nom_conjoint')}/></div>
                <div className="fg">
                  <label>Nombre d'enfants</label>
                  <input type="number" value={form.enfants.length} readOnly style={{ background:'#f0f4f8', color:'#1565C0', fontWeight:700, cursor:'default' }}/>
                </div>
              </div>
              <div style={{ fontWeight:700, fontSize:13, color:'#1a3a5c', margin:'10px 0 6px' }}>
                Enfants à charge <span style={{ fontWeight:400, fontSize:11, color:'#888' }}>({form.enfants.length} enregistré{form.enfants.length !== 1 ? 's' : ''})</span>
              </div>
              {form.enfants.map((enf, i) => (
                <div key={i} className="form-grid" style={{ background:'#f8fafc', padding:10, borderRadius:6, marginBottom:8, border:'1px solid #e8edf3' }}>
                  <div className="fg">
                    <label>Nom & Prénoms</label>
                    <input value={enf.nom_prenoms} onChange={e => { const enfants = [...form.enfants]; enfants[i] = { ...enfants[i], nom_prenoms: e.target.value }; setForm(f => ({ ...f, enfants })); }}/>
                  </div>
                  <div className="fg" style={{ flexDirection:'row', alignItems:'flex-end', gap:8 }}>
                    <div style={{ flex:1 }}><label>Date de naissance</label><input type="date" value={enf.date_naissance || ''} onChange={e => { const enfants = [...form.enfants]; enfants[i] = { ...enfants[i], date_naissance: e.target.value }; setForm(f => ({ ...f, enfants })); }}/></div>
                    <button className="btn btn-red btn-sm" onClick={() => setForm(f => ({ ...f, enfants: f.enfants.filter((_, j) => j !== i) }))}>✕</button>
                  </div>
                </div>
              ))}
              <button className="btn btn-outline btn-sm" onClick={() => setForm(f => ({ ...f, enfants: [...f.enfants, { nom_prenoms:'', date_naissance:'' }] }))}>+ Ajouter un enfant</button>
            </div>
          )}

          {formTab === 'rh' && (
            <div className="card">
              <div className="card-title">Informations RH <span style={{ fontSize:11, color:'#888', fontWeight:400 }}>(à remplir par les RH)</span></div>

              <div className="form-grid">
                <div className="fg">
                  <label>Matricule GFM3</label>
                  <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                    <input
                      value={form.matricule}
                      readOnly={!editId} // auto-généré en création, éditable en modification
                      onChange={f('matricule')}
                      style={{
                        fontFamily:'monospace', fontWeight:700, letterSpacing:1,
                        background: editId ? '' : '#f0f9ff',
                        color:'#1565C0', border:'1.5px solid #93c5fd',
                        flex:1,
                      }}
                      placeholder="EMP-00001"
                    />
                    {!editId && (
                      <span title="Généré automatiquement" style={{ fontSize:18 }}>🔒</span>
                    )}
                  </div>
                </div>
                <div className="fg"><label>Type de contrat</label><select value={form.type_contrat} onChange={f('type_contrat')}>{TYPES_CONTRAT.map(t => <option key={t}>{t}</option>)}</select></div>
              </div>

              <div className="form-grid">
                <div className="fg">
                  <label>Catégorie professionnelle <span style={{ fontSize:10, background:'#dbeafe', color:'#1e40af', borderRadius:10, padding:'1px 6px', fontWeight:700, marginLeft:4 }}>§3.1</span></label>
                  <select value={form.categorie} onChange={f('categorie')}>
                    <option value="">—</option>
                    {['A1','A2','B1','B2','C1','C2','D','Agent de maîtrise','Cadre','Cadre supérieur','Directeur'].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="fg">
                  <label>Projet principal <span style={{ fontSize:10, background:'#dbeafe', color:'#1e40af', borderRadius:10, padding:'1px 6px', fontWeight:700, marginLeft:4 }}>§3.1</span></label>
                  <select value={form.projet_id} onChange={f('projet_id')}>
                    <option value="">— Aucun projet —</option>
                    {projets.map(p => <option key={p.id} value={p.id}>{p.nom}{p.bailleur ? ` — ${p.bailleur}` : ''}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-grid">
                <div className="fg"><label>Date de début</label><input type="date" value={form.date_debut} onChange={f('date_debut')}/></div>
                <div className="fg"><label>Date de fin</label><input type="date" value={form.date_fin} onChange={f('date_fin')}/></div>
              </div>

              <div style={{ fontWeight:700, fontSize:13, color:'#1a3a5c', margin:'12px 0 8px', paddingTop:10, borderTop:'1px solid #e8edf3' }}>
                Période d'essai <span style={{ fontSize:10, background:'#dbeafe', color:'#1e40af', borderRadius:10, padding:'1px 6px', fontWeight:700 }}>§3.1.1</span>
              </div>
              <div className="form-grid">
                <div className="fg"><label>Début essai</label><input type="date" value={form.date_debut_essai} onChange={f('date_debut_essai')}/></div>
                <div className="fg"><label>Fin essai</label><input type="date" value={form.date_fin_essai} onChange={f('date_fin_essai')}/></div>
              </div>

              <div className="form-grid">
                <div className="fg"><label>Lieu de service</label><input value={form.lieu_service} onChange={f('lieu_service')}/></div>
                <div className="fg"><label>Lieu de recrutement</label><input value={form.lieu_recrutement} onChange={f('lieu_recrutement')}/></div>
              </div>
              <div style={{ fontWeight:700, fontSize:13, color:'#1a3a5c', margin:'12px 0 8px', paddingTop:10, borderTop:'1px solid #e8edf3' }}>
                Éléments de salaire <span style={{ fontSize:10, background:'#dbeafe', color:'#1e40af', borderRadius:10, padding:'1px 6px', fontWeight:700 }}>§3.13</span>
                <span style={{ fontSize:11, fontWeight:400, color:'#888', marginLeft:8 }}>Repris automatiquement à la création du bulletin de paie</span>
              </div>
              <div className="form-grid">
                <div className="fg"><label>Salaire de base (FCFA)</label><input type="number" value={form.salaire_base} onChange={f('salaire_base')} min="0"/></div>
                <div className="fg"><label>Statut</label><select value={form.statut} onChange={f('statut')}><option>Actif</option><option>Inactif</option><option>Suspendu</option></select></div>
              </div>
              <div className="form-grid">
                <div className="fg"><label>Sursalaire (FCFA)</label><input type="number" value={form.sursalaire} onChange={f('sursalaire')} min="0"/></div>
                <div className="fg"><label>Indemnité de représentation (FCFA)</label><input type="number" value={form.indemnite_representation} onChange={f('indemnite_representation')} min="0"/></div>
                <div className="fg"><label>Indemnité de transport (FCFA)</label><input type="number" value={form.indemnite_transport} onChange={f('indemnite_transport')} min="0"/></div>
              </div>

              {/* §3.30 — Répartition du coût par projet (compacte, max 5) */}
              <div style={{ fontWeight:700, fontSize:13, color:'#1a3a5c', margin:'12px 0 8px', paddingTop:10, borderTop:'1px solid #e8edf3' }}>
                Répartition du coût par projet <span style={{ fontSize:10, background:'#dcfce7', color:'#166534', borderRadius:10, padding:'1px 6px', fontWeight:700 }}>§3.30</span>
                <span style={{ fontSize:11, fontWeight:400, color:'#888', marginLeft:8 }}>Jusqu'à 5 projets. Reprise automatiquement dans le bulletin. La part GFM3 = complément à 100 %.</span>
              </div>
              {(form.repartition_projets || []).map((rp, i) => (
                <div key={i} style={{ display:'flex', gap:6, marginBottom:6, alignItems:'center', maxWidth:520 }}>
                  <select value={rp.projet_id} style={{ flex:1, minWidth:0, padding:'5px 6px', fontSize:12 }}
                    onChange={e => setForm(p => ({ ...p, repartition_projets: p.repartition_projets.map((x, idx) => idx === i ? { ...x, projet_id: e.target.value } : x) }))}>
                    <option value="">— Choisir un projet —</option>
                    {projets.map(pr => <option key={pr.id} value={pr.id}>{pr.nom}{pr.bailleur ? ` — ${pr.bailleur}` : ''}</option>)}
                  </select>
                  <input type="number" min="0" max="100" placeholder="%" value={rp.pourcentage} style={{ width:64, padding:'5px 6px', fontSize:12 }}
                    onChange={e => setForm(p => ({ ...p, repartition_projets: p.repartition_projets.map((x, idx) => idx === i ? { ...x, pourcentage: e.target.value } : x) }))}/>
                  <span style={{ fontSize:12, color:'#6b7a93' }}>%</span>
                  <button type="button" className="btn btn-red btn-sm" style={{ padding:'3px 8px' }}
                    onClick={() => setForm(p => ({ ...p, repartition_projets: p.repartition_projets.filter((_, idx) => idx !== i) }))}>✕</button>
                </div>
              ))}
              {(form.repartition_projets || []).length < 5 && (
              <button type="button" className="btn btn-gray btn-sm" style={{ padding:'4px 10px' }}
                onClick={() => setForm(p => ({ ...p, repartition_projets: [ ...(p.repartition_projets || []), { projet_id:'', pourcentage:'' } ] }))}>
                + Ajouter un projet
              </button>
              )}
              {(form.repartition_projets || []).length >= 5 && (
                <div style={{ fontSize:11, color:'#b45309', margin:'4px 0' }}>Maximum 5 projets par employé atteint.</div>
              )}
              {/* §3.57 — contrôle du total : projets + part GFM3 = 100 % */}
              {(() => {
                const somme = (form.repartition_projets || [])
                  .reduce((s, x) => s + (parseFloat(x.pourcentage) || 0), 0);
                const depasse = somme > 100.0001;
                const partGFM3 = Math.max(0, 100 - somme);
                return (
                  <div style={{ margin:'6px 0 4px' }}>
                    <div style={{ fontSize:11, color: depasse ? '#b91c1c' : '#6b7a93' }}>
                      Total projets : <strong>{somme.toFixed(1)}%</strong>
                      &nbsp;+&nbsp;Part GFM3 (calculée automatiquement) : <strong>{partGFM3.toFixed(1)}%</strong>
                      &nbsp;=&nbsp;<strong>{Math.max(somme, 100).toFixed(1)}%</strong>
                    </div>
                    {depasse && (
                      <div style={{ marginTop:6, padding:'8px 12px', borderRadius:6,
                                    background:'#fef2f2', border:'1px solid #fecaca',
                                    color:'#b91c1c', fontSize:12, fontWeight:600 }}>
                        ⚠ La somme des pourcentages atteint {somme.toFixed(1)}% : elle ne peut pas
                        dépasser 100%. L'enregistrement est bloqué tant que ce n'est pas corrigé
                        (retirez {(somme - 100).toFixed(1)} point{somme - 100 > 1 ? 's' : ''}).
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="form-grid">
                <div className="fg"><label>Département</label><select value={form.departement_id} onChange={f('departement_id')}><option value="">—</option>{depts.map(d => <option key={d.id} value={d.id}>{d.nom}</option>)}</select></div>
                <div className="fg"><label>Fonction</label><select value={form.fonction_id} onChange={f('fonction_id')}><option value="">—</option>{fonctions.map(fn => <option key={fn.id} value={fn.id}>{fn.intitule}</option>)}</select></div>
              </div>

              <div style={{ fontWeight:700, fontSize:13, color:'#1a3a5c', margin:'12px 0 8px', paddingTop:10, borderTop:'1px solid #e8edf3' }}>
                Affectation bureau <span style={{ fontSize:10, background:'#dcfce7', color:'#166534', borderRadius:10, padding:'1px 6px', fontWeight:700 }}>§Bureau</span>
              </div>
              <div className="form-grid">
                <div className="fg">
                  <label>Bureau d'affectation</label>
                  <select value={form.bureau_id} onChange={f('bureau_id')}>
                    <option value="">— Aucun bureau —</option>
                    {bureaux.map(b => <option key={b.id} value={b.id}>{b.est_siege ? '🏢' : '📍'} {b.nom} ({b.ville})</option>)}
                  </select>
                </div>
              </div>

              <div style={{ fontWeight:700, fontSize:13, color:'#1a3a5c', margin:'12px 0 8px', paddingTop:10, borderTop:'1px solid #e8edf3' }}>
                Hiérarchie
              </div>
              <div className="form-grid">
                <div className="fg">
                  <label>Supérieur hiérarchique</label>
                  <select value={form.superieur_id} onChange={f('superieur_id')}>
                    <option value="">— Aucun —</option>
                    {superieurs.filter(s => s.id !== editId).map(s => (
                      <option key={s.id} value={s.id}>{s.nom} {s.prenoms} ({s.matricule})</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ fontWeight:700, fontSize:13, color:'#1a3a5c', margin:'12px 0 8px', paddingTop:10, borderTop:'1px solid #e8edf3' }}>
                Soldes de congé annuel <span style={{ fontSize:10, background:'#fef9c3', color:'#854d0e', borderRadius:10, padding:'1px 6px', fontWeight:700 }}>§3.46</span>
                <span style={{ fontSize:11, fontWeight:400, color:'#888', marginLeft:8 }}>
                  Le solde initial est saisi ici. Le solde actuel est calculé par les feuilles de temps.
                </span>
              </div>
              <div className="form-grid" style={{ gridTemplateColumns:'1fr 1fr 1fr' }}>
                <div className="fg">
                  <label>Solde initial — Congé annuel (CA)</label>
                  <input type="number" min="0" step="0.5" value={form.solde_ca_initial} onChange={f('solde_ca_initial')} style={{ borderColor:'#fde68a' }} />
                  <span style={{ fontSize:10.5, color:'#888' }}>Droits acquis à l'entrée (jours)</span>
                </div>
                <div className="fg">
                  <label>Solde annuel actuel</label>
                  <input
                    type="text"
                    readOnly
                    value={
                      form.solde_ca_actuel != null
                        ? `${Number(form.solde_ca_actuel).toFixed(1)} j`
                        : '—'
                    }
                    style={{ borderColor:'#bbf7d0', background:'#f0fdf4', color:'#15803d',
                             fontWeight:700, cursor:'not-allowed' }}
                  />
                  <span style={{ fontSize:10.5, color:'#888' }}>
                    Report des feuilles de temps — non modifiable
                  </span>
                </div>
                <div className="fg">
                  <label>Congés pris (année en cours)</label>
                  <input
                    type="text"
                    readOnly
                    value={
                      form.conges_ca_pris != null
                        ? `${Number(form.conges_ca_pris).toFixed(1)} j`
                        : '—'
                    }
                    style={{ borderColor:'#e2e8f0', background:'#f8fafc', color:'#475569',
                             fontWeight:700, cursor:'not-allowed' }}
                  />
                  <span style={{ fontSize:10.5, color:'#888' }}>Cumul des CA posés</span>
                </div>
              </div>

              <div style={{ fontWeight:700, fontSize:13, color:'#1a3a5c', margin:'14px 0 8px', paddingTop:10, borderTop:'1px solid #e8edf3' }}>Renouvellements de contrat</div>
              <div className="form-grid" style={{ marginBottom:8 }}>
                <div className="fg"><label>Nombre de renouvellements</label><input type="number" min="0" max="3" value={form.nb_renouvellements} onChange={f('nb_renouvellements')}/></div>
              </div>
              {[1,2,3].map(n => {
                const nbR = parseInt(form.nb_renouvellements) || 0;
                if (n > nbR) return null;
                return (
                  <div key={n} style={{ background:'#f8fafc', padding:10, borderRadius:6, marginBottom:8, border:'1px solid #e8edf3' }}>
                    <div style={{ fontSize:12, fontWeight:700, color:'#1a3a5c', marginBottom:6 }}>{n}. Avenant 0/{n}</div>
                    <div className="form-grid">
                      <div className="fg"><label>Date début</label><input type="date" value={form[`renouv${n}_date_debut`]} onChange={f(`renouv${n}_date_debut`)}/></div>
                      <div className="fg"><label>Date fin</label><input type="date" value={form[`renouv${n}_date_fin`]} onChange={f(`renouv${n}_date_fin`)}/></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="actions-bar">
            <button className="btn btn-gray" onClick={() => setSubTab('list')}>Annuler</button>
            <button className="btn btn-green" onClick={handleSave} disabled={saving}>
              {saving ? '⏳…' : editId ? '💾 Enregistrer les modifications' : '+ Enregistrer la fiche'}
            </button>
          </div>
        </>
      )}

      {/* ════ VUE DÉTAIL ════ */}
      {subTab === 'view' && viewData && (
        <div className="card">
          <div className="card-title" style={{ display:'flex', justifyContent:'space-between' }}>
            <span>Fiche de {viewData.nom} {viewData.prenoms} — {viewData.matricule}</span>
            <div style={{ display:'flex', gap:8 }}>
              <button className="btn btn-blue btn-sm" onClick={() => openEdit(viewData.id)}>✏️ Modifier</button>
              <button
                className="btn btn-sm"
                style={{
                  background: viewData.statut_emploi === 'parti' ? '#fee2e2'
                            : viewData.statut_emploi === 'depart_prevu' ? '#fff7ed'
                            : '#f3f4f6',
                  color: viewData.statut_emploi === 'parti' ? '#991b1b'
                       : viewData.statut_emploi === 'depart_prevu' ? '#c2410c'
                       : '#374151',
                  border: '1px solid #d1d5db',
                }}
                onClick={() => setShowModalDepart(true)}
              >
                {viewData.statut_emploi === 'parti' ? '📋 Départ enregistré'
                 : viewData.statut_emploi === 'depart_prevu' ? '⚠️ Départ prévu'
                 : '🚪 Gérer le départ'}
              </button>
              <button className="btn btn-gray btn-sm" onClick={() => setSubTab('list')}>← Retour</button>
            </div>
          </div>

          {/* Sous-onglets vue */}
          <div className="sub-tabs" style={{ margin:'0 0 14px' }}>
            <button className={`sub-tab${viewTab === 'info' ? ' active' : ''}`} onClick={() => setViewTab('info')}>📋 Informations</button>
            <button className={`sub-tab${viewTab === 'historique' ? ' active' : ''}`} onClick={() => setViewTab('historique')}>
              🔄 Historique postes
              {historique.length > 0 && <span className="nav-badge" style={{ marginLeft:6 }}>{historique.length}</span>}
            </button>
            <button className={`sub-tab${viewTab === 'documents' ? ' active' : ''}`} onClick={() => setViewTab('documents')}>
              📄 Documents
              {documents.length > 0 && <span className="nav-badge" style={{ marginLeft:6 }}>{documents.length}</span>}
            </button>
          </div>

          {/* ─ Informations générales ─ */}
          {viewTab === 'info' && (
            <>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                {[
                  ['Matricule', viewData.matricule],
                  ['Statut', <span key="s" className={`badge badge-${viewData.statut === 'Actif' ? 'green' : 'red'}`}>{viewData.statut}</span>],
                  ['Statut emploi', viewData.statut_emploi === 'actif' ? <span key="se" style={{background:'#d1fae5',color:'#065f46',padding:'2px 10px',borderRadius:20,fontSize:11,fontWeight:700}}>● Actif</span>
                    : viewData.statut_emploi === 'parti' ? <span key="se" style={{background:'#fee2e2',color:'#991b1b',padding:'2px 10px',borderRadius:20,fontSize:11,fontWeight:700}}>● Parti(e) — {viewData.date_depart || ''}</span>
                    : viewData.statut_emploi === 'depart_prevu' ? <span key="se" style={{background:'#fff7ed',color:'#c2410c',padding:'2px 10px',borderRadius:20,fontSize:11,fontWeight:700}}>⚠ Départ prévu — {viewData.date_depart || ''}</span>
                    : <span key="se">—</span>],
                  ['Nom complet', `${viewData.nom} ${viewData.prenoms}`],
                  ['Sexe', viewData.sexe],
                  ['Date naissance', viewData.date_naissance],
                  ['Lieu naissance', viewData.lieu_naissance],
                  ['Nationalité', viewData.nationalite],
                  ['Groupe sanguin', viewData.groupe_sanguin],
                  ['N° CNPS', viewData.num_cnps],
                  ['Pièce — Type', viewData.type_piece],
                  ['Pièce — N°', viewData.num_piece],
                  ['Pièce — Expiration', viewData.date_expiration_piece ? fmtDate(viewData.date_expiration_piece) : '—'],
                  ['Téléphone', viewData.telephone],
                  ['Email', viewData.email],
                  ['Département', viewData.departement],
                  ['Fonction', viewData.fonction],
                  ['Catégorie professionnelle', viewData.categorie || '—'],
                  ['Projet principal', viewData.projet_nom || (viewData.projet_id ? `#${viewData.projet_id}` : '—')],
                  ['Type contrat', viewData.type_contrat],
                  ['Salaire base', viewData.salaire_base ? new Intl.NumberFormat('fr-CI').format(viewData.salaire_base) + ' FCFA' : '—'],
                  ['Début contrat', fmtDate(viewData.date_debut)],
                  ['Fin contrat', viewData.type_contrat === 'CDI' ? 'CDI (illimité)' : fmtDate(viewData.date_fin)],
                  ['Fin période essai', fmtDate(viewData.date_fin_essai)],
                  ['Essai confirmé', viewData.essai_confirme ? '✅ Oui' : (viewData.date_fin_essai ? '⏳ En cours' : '—')],
                  ["Bureau d'affectation", viewData.bureau_nom
                    ? `${viewData.est_siege ? '🏢' : '📍'} ${viewData.bureau_nom} — ${viewData.bureau_ville || ''}`
                    : '—'],
                  ['Supérieur hiérarchique', viewData.superieur_nom
                    ? `${viewData.superieur_nom} ${viewData.superieur_prenoms || ''}`.trim() + (viewData.superieur_matricule ? ` (${viewData.superieur_matricule})` : '')
                    : '—'],
                  ['Solde initial CA', viewData.solde_ca_initial != null ? `${viewData.solde_ca_initial} j` : '—'],
                  ['Solde CA actuel', viewData.solde_ca_actuel != null ? `${Number(viewData.solde_ca_actuel).toFixed(1)} j` : '—'],
                  ['Congés CA pris', viewData.conges_ca_pris != null ? `${Number(viewData.conges_ca_pris).toFixed(1)} j` : '—'],
                ].map(([l, v], i) => (
                  <div key={i} style={{ background:'#f8fafc', borderRadius:6, padding:'8px 12px', border:'1px solid #e8edf3' }}>
                    <div style={{ fontSize:11, color:'#888', marginBottom:2 }}>{l}</div>
                    <div style={{ fontSize:13, fontWeight:600 }}>{v || '—'}</div>
                  </div>
                ))}
              </div>

              {viewData.enfants?.length > 0 && (
                <div style={{ marginTop:12 }}>
                  <div style={{ fontWeight:700, fontSize:13, color:'#1a3a5c', marginBottom:6 }}>Enfants ({viewData.enfants.length})</div>
                  {viewData.enfants.map((e, i) => (
                    <div key={i} style={{ fontSize:13, padding:'4px 0', borderBottom:'1px solid #eef0f3' }}>
                      {e.nom_prenoms} {e.date_naissance && <span style={{ color:'#888', fontSize:11 }}>— né(e) le {e.date_naissance}</span>}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ─ Historique des postes §3.1.2 ─ */}
          {viewTab === 'historique' && (
            <div>
              {/* Barre d'action */}
              {!showMvtForm && !editMvt && (
                <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:12 }}>
                  <button className="btn btn-blue btn-sm" onClick={() => { setShowMvtForm(true); setEditMvt(null); }}>
                    + Ajouter un mouvement
                  </button>
                </div>
              )}

              {/* Formulaire ajout */}
              {showMvtForm && !editMvt && (
                <MouvementForm
                  empId={viewData.id}
                  depts={depts}
                  fonctions={fonctions}
                  onSaved={() => {
                    setShowMvtForm(false);
                    reloadHistorique(viewData.id);
                    showToast('Mouvement enregistré ✓');
                  }}
                  onCancel={() => setShowMvtForm(false)}
                />
              )}

              {/* Formulaire édition */}
              {editMvt && (
                <MouvementForm
                  empId={viewData.id}
                  mvt={editMvt}
                  depts={depts}
                  fonctions={fonctions}
                  onSaved={() => {
                    setEditMvt(null);
                    reloadHistorique(viewData.id);
                    showToast('Mouvement mis à jour ✓');
                  }}
                  onCancel={() => setEditMvt(null)}
                />
              )}

              {/* Tableau mouvements */}
              {historique.length === 0
                ? <div className="empty" style={{ padding:'2rem' }}>Aucun mouvement enregistré</div>
                : (
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Date effet</th>
                          <th>Type mouvement</th>
                          <th>Département</th>
                          <th>Fonction</th>
                          <th>Ancien grade</th>
                          <th>Nouveau grade</th>
                          <th>Motif</th>
                          <th style={{ textAlign:'center' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historique.map((h, i) => (
                          <tr key={h.id ?? i} style={{ opacity: editMvt?.id === h.id ? 0.4 : 1 }}>
                            <td style={{ fontFamily:'monospace', fontSize:12 }}>{fmtDate(h.date_effet)}</td>
                            <td>
                              <span className={mvtBadgeClass(h.type_mouvement)}>
                                {h.type_mouvement}
                              </span>
                            </td>
                            <td style={{ fontSize:12 }}>{h.departement || '—'}</td>
                            <td style={{ fontSize:12 }}>{h.fonction || '—'}</td>
                            <td style={{ fontSize:12 }}>{h.ancien_grade || '—'}</td>
                            <td style={{ fontSize:12 }}>{h.nouveau_grade || '—'}</td>
                            <td style={{ fontSize:12, color:'#555', maxWidth:200 }}>{h.motif || '—'}</td>
                            <td style={{ textAlign:'center', whiteSpace:'nowrap' }}>
                              <button
                                    className="btn-icon"
                                    title="Modifier"
                                    style={{ color:'#1565c0', marginRight:4 }}
                                    onClick={() => { setEditMvt(h); setShowMvtForm(false); }}
                                  >
                                    <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
                                      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                                    </svg>
                                  </button>
                                  <button
                                    className="btn-icon btn-icon-del"
                                    title="Supprimer"
                                    onClick={() => {
                                      if (!window.confirm(`Supprimer ce mouvement (${h.type_mouvement} — ${fmtDate(h.date_effet)}) ?`)) return;
                                      deleteHistoriquePoste(viewData.id, h.id)
                                        .then(() => {
                                          reloadHistorique(viewData.id);
                                          showToast('Mouvement supprimé');
                                        })
                                        .catch(() => showToast('Erreur suppression', 'error'));
                                    }}
                                  >
                                    <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
                                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                                    </svg>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
            </div>
          )}

          {/* ─ Documents administratifs §3.1.3 ─ */}
          {viewTab === 'documents' && (
            <div>
              {!showDocForm && !editDoc && (
                <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:12 }}>
                  <button className="btn btn-blue btn-sm" onClick={() => { setShowDocForm(true); setEditDoc(null); }}>
                    + Ajouter un document
                  </button>
                </div>
              )}

              {showDocForm && !editDoc && (
                <DocumentForm
                  empId={viewData.id}
                  onSaved={() => {
                    setShowDocForm(false);
                    reloadDocs(viewData.id);
                    showToast('Document enregistré ✓');
                  }}
                  onCancel={() => setShowDocForm(false)}
                />
              )}

              {editDoc && (
                <DocumentForm
                  empId={viewData.id}
                  doc={editDoc}
                  onSaved={() => {
                    setEditDoc(null);
                    reloadDocs(viewData.id);
                    showToast('Document mis à jour ✓');
                  }}
                  onCancel={() => setEditDoc(null)}
                />
              )}

              {documents.length === 0
                ? <div className="empty" style={{ padding:'2rem' }}>Aucun document enregistré</div>
                : (
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Type</th><th>Intitulé</th><th>Émission</th><th>Expiration</th>
                          <th>Statut</th><th>Fichier</th><th style={{ textAlign:'center' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {documents.map((d, i) => {
                          const sc = docStatutColor(d.statut);
                          return (
                            <tr key={d.id ?? i} style={{ opacity: editDoc?.id === d.id ? 0.4 : 1 }}>
                              <td style={{ fontSize:12 }}>{d.type_document}</td>
                              <td style={{ fontWeight:600 }}>{d.intitule}</td>
                              <td style={{ fontFamily:'monospace', fontSize:12 }}>{fmtDate(d.date_emission)}</td>
                              <td style={{ fontFamily:'monospace', fontSize:12 }}>{fmtDate(d.date_expiration)}</td>
                              <td>
                                <span style={{ ...sc, padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:700, display:'inline-block' }}>
                                  {d.statut}
                                </span>
                              </td>
                              <td style={{ fontSize:12 }}>
                                {d.fichier_chemin
                                  ? <a href={`/gfm3/backend/${d.fichier_chemin}`} target="_blank" rel="noreferrer"
                                      title={d.fichier_nom} style={{ color:'#1565c0', textDecoration:'none', fontWeight:600 }}>
                                      📥 {d.fichier_nom?.length > 20 ? d.fichier_nom.slice(0,18)+'…' : d.fichier_nom}
                                    </a>
                                  : <span style={{ color:'#aaa', fontSize:11 }}>—</span>}
                              </td>
                              <td style={{ textAlign:'center', whiteSpace:'nowrap' }}>
                                <button className="btn-icon" title="Modifier" style={{ color:'#1565c0', marginRight:4 }}
                                  onClick={() => { setEditDoc(d); setShowDocForm(false); }}>
                                  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
                                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                                  </svg>
                                </button>
                                <button className="btn-icon btn-icon-del" title="Supprimer"
                                  onClick={() => {
                                    if (!window.confirm(`Supprimer "${d.intitule}" ?`)) return;
                                    deleteDocumentRH(viewData.id, d.id)
                                      .then(() => { setDocuments(prev => prev.filter(x => x.id !== d.id)); showToast('Document supprimé'); })
                                      .catch(() => showToast('Erreur suppression', 'error'));
                                  }}>
                                  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
                                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                                  </svg>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
            </div>
          )}
        </div>
      )}

      {/* ── Modal Départ §Départ ── */}
      {showModalDepart && viewData && (
        <ModalDepart
          employe={{
            id:             viewData.id,
            nom_complet:    `${viewData.nom} ${viewData.prenoms}`,
            matricule:      viewData.matricule,
            type_contrat:   viewData.type_contrat,
            statut_emploi:  viewData.statut_emploi || 'actif',
            date_depart:    viewData.date_depart    || '',
            motif_depart_id: viewData.motif_depart_id || '',
            commentaire_depart: viewData.commentaire_depart || '',
          }}
          onClose={() => setShowModalDepart(false)}
          onSaved={async () => {
            setShowModalDepart(false);
            // Recharger la fiche pour afficher le nouveau statut
            try {
              const r = await getEmploye(viewData.id);
              setViewData(r.data);
              showToast('Départ enregistré ✓');
            } catch (e) {
              showToast('Départ enregistré — rechargez la fiche', 'success');
            }
          }}
        />
      )}
    </>
  );
}
